import React, { useState, useEffect, useRef, useCallback } from "react";
import useWebSocketConnection, {
  StreamState,
} from "../hooks/useWebSocketConnection";
import { ReadyState } from "react-use-websocket";
import { WebsocketSummarySessionResponse } from "../types/websocket_types";
import { storeUserActivity } from "./RecommendationFeed/api_client/recommendation_api";
import {
  UserActivityType,
  ResourceType,
} from "./RecommendationFeed/types/activity_types";
import TextareaAutosize from "react-textarea-autosize";
import { ConfigProvider, Segmented, message } from "antd";
import {
  BulbOutlined,
  FieldTimeOutlined,
  SearchOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { useIdeasNodeStore, SessionMetadata } from "../stores/ideaNodesStore";
import {
  backButtonStyle,
  bottomRowContainer,
  chatPaneContainer,
  chatPaneContainerOpen,
  flexNodeAndChatContainer,
  mainNodesPaneContainer,
  mainNodesPaneContainerClosed,
  nodeOutline,
  textAreaTopContainer,
  textAreaInnerContainer,
  textareaInputBar,
  topGridContainer,
  topGridContainerChatPaneOpen,
  textareaSendButton,
  pullUpDownControl,
  pullUpDownControlContainer,
} from "./summary_session_styles";
import { ChatPane } from "../components/ChatPane";
import { sendExtensionMessage } from "@sharpana/auth/api_client/allauth";
import { IdeaNode, SummarizationMode } from "../stores/shared_types";
import { ThreadMode } from "./summarization_layouts/ThreadMode";
import { usethreadModeStore } from "../stores/threadModeStore";
import { css } from "@emotion/css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookmark } from "@fortawesome/free-solid-svg-icons";
import { isWebExtension, isMobileApp } from "@sharpana/shared/utils/web_environment";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { ShareModal } from "../components/ShareModal";
import {
  isMobileDevice,
  isWebShareSupported,
  shareViaWebShareApi,
} from "../utils/browserDetection";
import { buildShareUrl } from "../utils/shareUtils";

const showDebuggingUI = false;

export const SummarySession = (props: {
  contentToSummarize: string;
  contentLength: string;
  targetUrl: string;
  sessionId: string;
  summarizationMode: SummarizationMode;
  tabId?: string;
  resourceId?: string; // Added resourceId parameter for tracking user activity
  slugParam?: string;
}) => {
  const wsURL = `${window.location.protocol === "http:" ? "ws://" : "wss://"}${
    window.location.host
  }/ws/session/`;

  // useWebSocketConnection was the old persisten WS connection approach,
  // which has been deprecated in favor of ephemeral / one-shot ws connections
  // useWebSocketConnection({
  //   wsURL,
  // });

  const appendTextToChatPaneNode = useIdeasNodeStore(
    (state) => state.appendTextToChatPaneNode
  );
  const setSendJsonMessage = useIdeasNodeStore(
    (state) => state.setSendJsonMessage
  );
  const setSessionMetadata = useIdeasNodeStore(
    (state) => state.setSessionMetadata
  );
  const setShowDebuggingUI = useIdeasNodeStore(
    (state) => state.setShowDebuggingUI
  );
  const sessionMetadata = useIdeasNodeStore((state) => state.sessionMetadata);
  const setTabId = useIdeasNodeStore((state) => state.setTabId);
  const chatPaneOpen = useIdeasNodeStore((state) => state.chatPaneOpen);
  const setWsReadyState = useIdeasNodeStore((state) => state.setWsReadyState);
  const setReconnectWs = useIdeasNodeStore((state) => state.setReconnectWs);

  const cleanState = usethreadModeStore((state) => state.cleanState);

  const summarizationMode = useIdeasNodeStore(
    (state) => state.summarizationMode
  );
  const setSummarizationMode = useIdeasNodeStore(
    (state) => state.setSummarizationMode
  );

  // Make body and root not scrollable when the chatPane is open
  // (since there's a small gap near the top where users could accidentally swipe and get everything behind moving jankily)
  useEffect(() => {
    const root = document.documentElement; // <html>
    const body = document.body;

    if (chatPaneOpen) {
      // save originals
      const rootOverflow = root.style.overflow;
      const bodyOverflow = body.style.overflow;

      root.style.overflow = "hidden";
      body.style.overflow = "hidden";

      // stop rubber-band bounce as well
      root.style.overscrollBehavior = "none";

      return () => {
        root.style.overflow = rootOverflow;
        body.style.overflow = bodyOverflow;
        root.style.overscrollBehavior = "";
      };
    }
  }, [chatPaneOpen]);

  const ifYoutubeFetchVideoId = (urlString: string) => {
    const url = new URL(urlString);

    const youtubeHosts = [
      "www.youtube.com",
      "youtube.com",
      "youtu.be",
      "www.youtube-nocookie.com",
    ];
    if (youtubeHosts.includes(url.host)) {
      const videoId = new URLSearchParams(url.search).get("v");
      return videoId;
    }
  };

  // Set summarization node from props if we haven't initialized it yet
  useEffect(() => {
    if (!summarizationMode) {
      // If a value is already set, we skip, otherwise initialize with value from props
      setSummarizationMode(props.summarizationMode);
    }
  }, [setSummarizationMode, summarizationMode, props.summarizationMode]);

  // Keep URL search args in sync with summarizationMode (needed for the social sharing feature to work correctly)
  const [, setSearchParams] = useSearchParams(); // we only need the setter
  useEffect(() => {
    if (
      summarizationMode === "threadInsights" ||
      summarizationMode === "threadTimeline"
    ) {
      // Create a new URLSearchParams object with current params
      const newParams = new URLSearchParams(window.location.search);

      // Set just the 'm' parameter based on the summarizationMode
      if (summarizationMode === "threadInsights") {
        newParams.set("m", "i");
      } else {
        newParams.set("m", "t");
      }

      // Update the search parameters while preserving others and also preserve history (instead of pushing new navigation)
      setSearchParams(newParams, { replace: true });
    }
  }, [summarizationMode, setSearchParams]);

  useEffect(() => {
    // Pass sendJsonMessage to store so we can call it from other components
    setSendJsonMessage(() => {});
  }, [setSendJsonMessage]);

  useEffect(() => {
    cleanState(); // When targetUrl or contentToSummarize changes we clean the thread store (which will trigger ThreadMode.tsx::useEffect() to re-request the initial node)
  }, [props.targetUrl, props.contentToSummarize]);

  useEffect(() => {
    const _sessionMetadata = {
      targetUrl: props.targetUrl,
      sessionId: props.sessionId,
      targetContent: props.contentToSummarize,
      resourceId: props.resourceId,
      ytVideoId: ifYoutubeFetchVideoId(props.targetUrl),
      contentDuration: props.contentLength,
      slugParam: props.slugParam,
    } as SessionMetadata;

    setSessionMetadata(_sessionMetadata);
  }, [
    setSessionMetadata,
    props.sessionId,
    props.targetUrl,
    props.contentToSummarize,
    props.contentLength,
    props.resourceId,
    props.slugParam,
  ]);

  useEffect(() => {
    setShowDebuggingUI(showDebuggingUI);
  }, [setShowDebuggingUI]);

  useEffect(() => {
    if (props.tabId) {
      setTabId(props.tabId);
    }
  }, [setTabId, props.tabId]);

  // Track interaction when SummarySession is opened - this is more reliable than tracking at click time
  // because we know the user has actually reached the content

  // Use a ref to track if we've already sent tracking for this resource
  const hasTrackedInteractionRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Use resourceId if provided
    const trackingId = props.resourceId;

    // Only proceed if we have a tracking ID and haven't tracked it yet
    if (trackingId && !hasTrackedInteractionRef.current.has(trackingId)) {
      // Mark as tracked immediately to prevent duplicate tracking attempts
      // even if the effect runs twice (which happens in React.StrictMode)
      hasTrackedInteractionRef.current.add(trackingId);

      // Fully non-blocking tracking implementation with error handling
      // No async/await - just use promises directly to be non-blocking
      Promise.race([
        storeUserActivity(
          trackingId,
          ResourceType.YT_FULL_VIDEO,
          UserActivityType.INTERACTED,
          "" // Empty string for derived_from_resource_id since it's not YT_SHARP
        ),
        // Add a timeout as a fallback (3 seconds)
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ])
        .then(() => {
          console.debug(
            `Successfully tracked interaction for resource: ${trackingId}`
          );
        })
        .catch((error) => {
          console.error("Failed to track SummarySession interaction:", error);
          Sentry.captureException(error, {
            tags: {
              activity_type: UserActivityType.INTERACTED,
              resource_type: ResourceType.YT_FULL_VIDEO,
              resource_id: trackingId,
            },
          });
        });
    }
  }, [props.resourceId]);

  useEffect(() => {
    // Update WebSocket ready state in store
    setWsReadyState(ReadyState.UNINSTANTIATED);
  }, [setWsReadyState]);

  useEffect(() => {
    // Store reconnectWs function for manual reconnection
    setReconnectWs(() => {});
  }, [setReconnectWs]);

  ////////////////////////////////////////////////////////////////////////////////
  ////////     Handle receiving a new JSON message from websocket:       /////////
  ////////////////////////////////////////////////////////////////////////////////

  useEffect(() => {}, []);

  ////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////

  const connectionStatus = "Uninstantiated";

  const isOnWebExtension = isWebExtension();
  const isOnMobileApp = isMobileApp();

  const summarizationModeLayoutMap: Record<SummarizationMode, any> = {
    threadTimeline: ThreadMode,
    threadInsights: ThreadMode,
  };

  const LayoutComponentClass = summarizationMode
    ? summarizationModeLayoutMap[summarizationMode]
    : null;

  let LayoutComponent: JSX.Element = <></>;
  if (LayoutComponentClass) {
    LayoutComponent = (
      <LayoutComponentClass contentToSummarize={props.contentToSummarize} />
    );
  }

  const topRowRef = useRef<HTMLDivElement>(null);
  const [topRowBottom, setTopRowBottom] = useState(0);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const updateMetrics = () => {
      if (topRowRef.current) {
        const rect = topRowRef.current.getBoundingClientRect();
        setTopRowBottom(Math.max(rect.bottom, 20)); // never allow negative, otherwise the pull up/down chat pane controls can slide out of view (and thus the chatpane could not be closed by the user)
      }
    };

    updateMetrics();
    window.addEventListener("resize", updateMetrics);
    window.addEventListener("scroll", updateMetrics);

    const resizeObserver = new ResizeObserver(updateMetrics);
    if (topRowRef.current) {
      resizeObserver.observe(topRowRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateMetrics);
      window.removeEventListener("scroll", updateMetrics);
      resizeObserver.disconnect();
    };
  }, []);

  // Get the streaming state from the store for testing purposes
  const chatPaneStreamingFinished_UsedInTesting = useIdeasNodeStore(
    (state) => state.chatPaneStreamingFinished_UsedInTesting
  );

  return (
    <div
      data-stream-state={StreamState.UNINSTANTIATED}
      data-chat-pane-has-finished={
        chatPaneStreamingFinished_UsedInTesting ? "true" : "false"
      }
      css={topGridContainer(isOnWebExtension, isOnMobileApp, chatPaneOpen)}
    >
      <TopRow
        ref={topRowRef}
        connectionStatus={connectionStatus}
        reconnectWs={() => {}}
        textAreaRef={textAreaRef}
        contentToSummarize={props.contentToSummarize}
        contentLength={props.contentLength}
        targetUrl={props.targetUrl}
        sessionId={props.sessionId}
        summarizationMode={props.summarizationMode}
        tabId={props.tabId}
        resourceId={props.resourceId}
        sessionMetadata={sessionMetadata}
      />

      <div css={flexNodeAndChatContainer}>
        {summarizationMode ? (
          <div css={mainNodesPaneContainer(isOnWebExtension)}>
            {LayoutComponent}
          </div>
        ) : (
          <></>
        )}
      </div>

      <BottomRow
        chatPaneOpen={chatPaneOpen}
        topRowBottom={topRowBottom}
        textAreaRef={textAreaRef}
      />
    </div>
  );
};

interface TopRowProps {
  connectionStatus: string;
  reconnectWs: () => void;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  contentToSummarize: string;
  contentLength: string;
  targetUrl: string;
  sessionId: string;
  summarizationMode: SummarizationMode;
  tabId?: string;
  resourceId?: string;
  sessionMetadata?: SessionMetadata;
}

const TopRow = React.forwardRef<HTMLDivElement, TopRowProps>(function TopRow(
  props,
  ref
) {
  const collapseAllToggles = useIdeasNodeStore(
    (state) => state.collapseAllToggles
  );
  const chatPaneOpen = useIdeasNodeStore((state) => state.chatPaneOpen);
  const setChatPaneOpen = useIdeasNodeStore((state) => state.setChatPaneOpen);
  const setSummarizationMode = useIdeasNodeStore(
    (state) => state.setSummarizationMode
  );
  const summarizationMode = useIdeasNodeStore(
    (state) => state.summarizationMode
  );
  const cleanState = usethreadModeStore((state) => state.cleanState);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backEnabled = searchParams.get("back_enabled");

  const _isWebExtension = isWebExtension();

  const highlightTest = () => {
    const webExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;
    sendExtensionMessage(webExtensionId, {
      message: "highlight_text",
      tabId: props.tabId,
      targetText: "AI stocks will dominate 2025.",
    });
  };

  const topRowStyle = css`
    display: grid;
    grid-template-columns: 80% 20%;
    margin: 14px auto;
    align-items: baseline;
    width: ${_isWebExtension ? "90%" : "100%"};
    margin-right: auto;
  `;

  const innerContainerIconStyle = css`
    position: absolute;
    right: 0px;
    top: 0;
    font-size: 1rem;
    margin-left: 12px;
    display: flex;
    gap: 8px;
    width: fit-content;
  `;

  const searchIconStyle = css`
    cursor: pointer;
    border: 1px solid #979797;
    border-radius: 6px;
    height: fit-content;
    padding: 0 4px;
    width: fit-content;
    width: 26px;
    text-align: center;
    margin-right: 4px;
    :hover {
      color: #1d72b9;
      border: 1px solid #1d72b9;
    }
  `;

  const searchIconContainer = css`
    height: 100%;
    position: relative;
    display: flex;
  `;

  const [shareModalVisible, setShareModalVisible] = useState(false);
  const { sessionMetadata } = props;
  const hasShareableLink = sessionMetadata?.slugParam !== undefined;

  const handleShare = () => {
    if (!hasShareableLink) return;

    const currentUrl = window.location.href;
    const url = new URL(currentUrl);

    // Ensure slugParam is in the URL
    if (!url.searchParams.has("s") && sessionMetadata?.slugParam) {
      url.searchParams.set("s", sessionMetadata.slugParam);
    }

    const shareUrl = url.toString();
    const shareTitle = `Sharpana summary: ${
      sessionMetadata?.targetUrl || "Content"
    }`;

    if (isMobileDevice() && isWebShareSupported()) {
      // Use Web Share API on mobile
      shareViaWebShareApi(shareUrl, shareTitle).catch((err) => {
        // Only show error if it's not a user cancellation
        if (err.name !== "AbortError") {
          message.error("Could not share content");
        }
      });
    } else {
      // Show share modal on desktop
      setShareModalVisible(true);
    }
  };

  return (
    <div ref={ref} className={topRowStyle}>
      <div
        className={css`
          display: flex;
        `}
      >
        {(backEnabled === "1" || backEnabled === "true") && (
          <button
            css={backButtonStyle}
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <ArrowLeftOutlined />
          </button>
        )}
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                itemSelectedBg: "#0c0c0c",
                colorText: "#f7f7f7",
                itemColor: "#f7f7f7",
                trackBg: "#3c3c3c",
              },
            },
          }}
        >
          <Segmented<string>
            value={summarizationMode}
            options={[
              {
                label: "Insights",
                value: "threadInsights",
                icon: <BulbOutlined />,
              },
              {
                label: "Timeline",
                value: "threadTimeline",
                icon: <FieldTimeOutlined />,
              },
            ]}
            onChange={(value) => {
              setSummarizationMode(value as SummarizationMode);
              if (value !== summarizationMode) {
                // When summarization mode changes we clean the thread store (which will trigger ThreadMode.tsx::useEffect() to re-request the initial node)
                cleanState();
                props.reconnectWs(); // Reconnecting stops the previous ws stream if a response was ongoing (so we don't have to wait for it to finish before starting new summarization mode's response)
              }
            }}
          />
        </ConfigProvider>
      </div>

      <div className={searchIconContainer}>
        <div className={innerContainerIconStyle}>
          {/* Share button - only shown when slugParam exists */}
          {hasShareableLink && (
            <div
              onClick={handleShare}
              className={searchIconStyle}
              title="Share this summary"
            >
              <ShareAltOutlined />
              <span></span>
            </div>
          )}

          {/* Search/chat button */}
          <div
            onClick={() => {
              if (!chatPaneOpen) {
                // We are about to open the chat pane, so focus on textarea
                // Use setTimeout to ensure the focus happens after the chat pane is opened
                setTimeout(() => {
                  props.textAreaRef.current?.focus();
                }, 50);
              }
              setChatPaneOpen(!chatPaneOpen);
            }}
            className={searchIconStyle}
            title="Chat with AI about this content"
          >
            <SearchOutlined />
            <span></span>
          </div>
        </div>

        {/* Share Modal for desktop sharing */}
        {hasShareableLink && (
          <ShareModal
            isOpen={shareModalVisible}
            onClose={() => setShareModalVisible(false)}
            url={buildShareUrl(
              window.location.href,
              sessionMetadata?.slugParam
            )}
            title={`Sharpana summary: ${
              sessionMetadata?.targetUrl || "Content"
            }`}
          />
        )}
      </div>

      {showDebuggingUI ? (
        <span>The WebSocket is currently {props.connectionStatus}</span>
      ) : (
        <></>
      )}
    </div>
  );
});

function BottomRow({
  chatPaneOpen,
  topRowBottom,
  textAreaRef,
}: {
  chatPaneOpen: boolean;
  topRowBottom: number;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
}) {
  const submitChatPaneNewUserUtterance = useIdeasNodeStore(
    (state) => state.submitChatPaneNewUserUtteranceEphemeral
  );
  const setChatPaneOpen = useIdeasNodeStore((state) => state.setChatPaneOpen);
  const chatPaneNodes = useIdeasNodeStore((state) => state.chatPaneNodes);
  const [bottomRowInputText, setBottomRowInputText] = useState("");
  const sendingRef = useRef(false);

  // Gesture handling for the pull up/down control
  const dragRef = useRef<{
    active: boolean;
    startY: number;
    pointerId: number;
    didSwipe: boolean;
  }>({ active: false, startY: 0, pointerId: -1, didSwipe: false });
  const ignoreClickRef = useRef(false);

  const SWIPE_THRESHOLD = 22; // px of vertical movement to count as a swipe

  const onHandlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only left button for mouse, allow touch/pen
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragRef.current = {
      active: true,
      startY: e.clientY,
      pointerId: e.pointerId,
      didSwipe: false,
    };
    // Capture this pointer so move/up go to this element even if finger moves off
    (e.currentTarget as any).setPointerCapture?.(e.pointerId);
  };

  const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const st = dragRef.current;
    if (!st.active || e.pointerId !== st.pointerId) return;
    const dy = e.clientY - st.startY;
    if (Math.abs(dy) > SWIPE_THRESHOLD) {
      // Act only once per gesture
      if (!st.didSwipe) {
        if (dy > 0) {
          // Swipe down -> close if open
          if (chatPaneOpen) setChatPaneOpen(false);
        } else {
          // Swipe up -> open if closed
          if (!chatPaneOpen) setChatPaneOpen(true);
        }
        st.didSwipe = true;
        // Prevent the subsequent click from toggling again
        ignoreClickRef.current = true;
        window.setTimeout(() => {
          ignoreClickRef.current = false;
        }, 330);
      }
      // Prevent page scroll while swiping on the handle
      e.preventDefault();
    }
  };

  const endPointer = (e: React.PointerEvent<HTMLDivElement>) => {
    const st = dragRef.current;
    if (st.active && e.pointerId === st.pointerId) {
      try {
        (e.currentTarget as any).releasePointerCapture?.(e.pointerId);
      } catch {}
      dragRef.current.active = false;
    }
  };

  const handleSend = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (sendingRef.current) return;
    sendingRef.current = true;
    // Swallow the very next click anywhere (ghost/retargeted click after touch/keyboard dismiss)
    const swallowNextClick = (ev: MouseEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      document.removeEventListener("click", swallowNextClick, true);
      if (removeTimer) clearTimeout(removeTimer);
    };
    document.addEventListener("click", swallowNextClick, true);
    const removeTimer = window.setTimeout(() => {
      document.removeEventListener("click", swallowNextClick, true);
    }, 400);
    // Ensure chat pane remains open immediately (before any async work)
    setChatPaneOpen(true);
    // Blur first to commit any un-applied IME/composition text, then read from DOM value
    textAreaRef.current?.blur();
    setTimeout(() => {
      const domValue = textAreaRef.current?.value ?? bottomRowInputText;
      submitChatPaneNewUserUtterance(domValue);
      setBottomRowInputText("");
      sendingRef.current = false;
      // Reinforce open state in case a retargeted click closed it
      setTimeout(() => setChatPaneOpen(true), 120);
    }, 0);
  };

  return (
    <div css={bottomRowContainer(isWebExtension())}>
      <div css={pullUpDownControlContainer}>
        <div /> {/* Empty div for grid layout */}
        {chatPaneNodes.length > 0 || chatPaneOpen ? (
          <div
            css={pullUpDownControl}
            // Swipes (up/down) and click both toggle the chat pane; swipe is directional (up=open, down=close)
            style={{ touchAction: "none" }}
            onPointerDown={onHandlePointerDown}
            onPointerMove={onHandlePointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onClick={(e) => {
              if (ignoreClickRef.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setChatPaneOpen(!chatPaneOpen);
            }}
          >
            {chatPaneOpen ? (
              <CaretDownOutlined style={{ fontSize: "16px" }} />
            ) : (
              <CaretUpOutlined style={{ fontSize: "16px" }} />
            )}
          </div>
        ) : (
          <div />
        )}
        <div /> {/* Empty div for grid layout */}
      </div>

      <div
        css={[
          chatPaneContainer,
          chatPaneOpen && chatPaneContainerOpen(topRowBottom),
        ]}
      >
        <ChatPane />
      </div>

      <div css={textAreaTopContainer}>
        {/* Clicking this container will focus the TextareaAutosize */}
        <div
          css={textAreaInnerContainer}
          onClick={() => {
            textAreaRef.current?.focus();
          }}
        >
          <TextareaAutosize
            ref={textAreaRef}
            minRows={1}
            maxRows={4}
            placeholder="Ask about the content..."
            css={textareaInputBar}
            value={bottomRowInputText}
            onChange={(e) => setBottomRowInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                // Ensure chat pane remains open
                setChatPaneOpen(true);
                submitChatPaneNewUserUtterance(bottomRowInputText);
                setBottomRowInputText("");
                // Hide mobile soft keyboard by blurring the textarea
                textAreaRef.current?.blur();
                // Reinforce open state in case a retargeted click closed it
                setTimeout(() => setChatPaneOpen(true), 120);
              }
            }}
          />

          <button
            type="button"
            onTouchStart={handleSend}
            onMouseDown={handleSend}
            onClick={handleSend}
            css={textareaSendButton}
          >
            <ArrowRightOutlined />
          </button>
        </div>
      </div>
    </div>
  );
}
